import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { useMusicPlayer } from '../context/MusicPlayerContext';

// Music API Service
const MusicAPI = {
  getAllSongs: async (page = 1, limit = 100) => {
    try {
      const response = await fetch(`https://samamusic.onrender.com/sama-music/musics/getAll?page=${page}&limit=${limit}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching songs:', error);
      throw error;
    }
  },
};

// Utility function to format time
function mmss(sec) {
  const sInt = Math.max(0, Math.floor(sec || 0));
  const m = Math.floor(sInt / 60);
  const s = (sInt % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

// Lazy Image Component
const LazyImage = ({ src, alt, className = '' }) => {
  const [loaded, setLoaded] = useState(false);
  
  return (
    <div className={`relative ${className}`}>
      {!loaded && (
        <div className="absolute inset-0 bg-slate-700 animate-pulse rounded-lg" />
      )}
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity ${
          loaded ? 'opacity-100' : 'opacity-0'
        } ${className}`}
        onLoad={() => setLoaded(true)}
        loading="lazy"
      />
    </div>
  );
};

export default function FavoritesPage() {
  const [songs, setSongs] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { playSong, currentSong, isPlaying } = useMusicPlayer();

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('musicPlayer-favorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  // Save favorites to localStorage
  useEffect(() => {
    localStorage.setItem('musicPlayer-favorites', JSON.stringify(favorites));
  }, [favorites]);

  // Fetch all songs
  useEffect(() => {
    const fetchSongs = async () => {
      try {
        setLoading(true);
        const data = await MusicAPI.getAllSongs(1, 100);
        
        if (data.success) {
          setSongs(data.data.data || []);
        } else {
          setError('Failed to load songs');
        }
      } catch (err) {
        console.error('Error fetching songs:', err);
        setError('Failed to load songs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSongs();
  }, []);

  // Filter favorite songs
  const favoriteSongs = songs.filter(song => favorites.includes(song._id));

  // Filter by search
  const filteredFavorites = favoriteSongs.filter(song =>
    song.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    song.artists?.some(artist =>
      artist.toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  // Handle song click - using centralized music player
  const handleSongClick = (song) => {
    try {
      // Format song data to match expected structure
      const formattedSong = {
        id: song._id,
        title: song.title || 'Unknown Song',
        artist: song.artists?.join(', ') || 'Unknown Artist',
        album: song.album || 'Unknown Album',
        cover: song.image || '/default-cover.jpg',
        audioUrl: song.audio,
        duration: mmss(song.durationInSeconds) || '3:20',
      };

      // Use the filtered favorites as playlist
      const formattedList = filteredFavorites.map(s => ({
        id: s._id,
        title: s.title || 'Unknown Song',
        artist: s.artists?.join(', ') || 'Unknown Artist',
        album: s.album || 'Unknown Album',
        cover: s.image || '/default-cover.jpg',
        audioUrl: s.audio,
        duration: mmss(s.durationInSeconds) || '3:20',
      }));

      // Play the song using centralized music player
      playSong(formattedSong, formattedList);
      console.log('Now playing:', formattedSong.title);
    } catch (error) {
      console.error('Error playing song:', error);
      setError('Failed to play song');
    }
  };

  const toggleFavorite = (songId, e) => {
    e?.stopPropagation();
    setFavorites(prev =>
      prev.includes(songId)
        ? prev.filter(id => id !== songId)
        : [...prev, songId]
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fuchsia-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100 pb-40">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-center gap-6">
          <div className="h-24 w-24 rounded-xl bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center shadow-2xl">
            <Heart className="w-12 h-12 text-white" fill="currentColor" />
          </div>
          <div>
            <h1 className="text-4xl font-bold mb-2">Favorite Songs</h1>
            <p className="text-slate-400 text-lg">
              {favoriteSongs.length} {favoriteSongs.length === 1 ? 'song' : 'songs'} in your collection
            </p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="px-6 mb-6">
        <div className="max-w-md">
          <div className="relative">
            <svg className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search your favorites..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white/10 border border-white/10 rounded-full py-3 pl-10 pr-4 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-fuchsia-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6">
        {error && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-200">
            {error}
          </div>
        )}

        {favoriteSongs.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="w-24 h-24 text-slate-600 mx-auto mb-6" />
            <h3 className="text-2xl font-medium text-slate-300 mb-3">No favorites yet</h3>
            <p className="text-slate-400 text-lg max-w-md mx-auto">
              Start building your collection by clicking the heart icon on your favorite songs
            </p>
          </div>
        ) : filteredFavorites.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-slate-400 text-lg">
              No favorites found matching "{searchQuery}"
            </div>
          </div>
        ) : (
          <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 text-left text-slate-400 text-sm">
                  <th className="p-4 w-12">#</th>
                  <th className="p-4">Title</th>
                  <th className="p-4 hidden sm:table-cell">Artists</th>
                  <th className="p-4 w-20 text-center"></th>
                  <th className="p-4 text-right w-20">
                    <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredFavorites.map((song, index) => (
                  <tr
                    key={song._id}
                    className={`border-b border-white/5 hover:bg-white/10 cursor-pointer transition ${
                      currentSong?.id === song._id ? 'bg-white/10' : ''
                    }`}
                    onClick={() => handleSongClick(song)}
                  >
                    <td className="p-4 w-12">
                      {currentSong?.id === song._id && isPlaying ? (
                        <div className="w-4 h-4 flex items-center justify-center">
                          <div className="w-1 h-3 bg-fuchsia-500 mx-0.5 animate-pulse"></div>
                          <div className="w-1 h-3 bg-fuchsia-500 mx-0.5 animate-pulse" style={{animationDelay: '0.1s'}}></div>
                          <div className="w-1 h-3 bg-fuchsia-500 mx-0.5 animate-pulse" style={{animationDelay: '0.2s'}}></div>
                        </div>
                      ) : (
                        <span className="text-slate-400">{index + 1}</span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 flex-shrink-0 rounded overflow-hidden">
                          <LazyImage
                            src={song.image}
                            alt={song.title}
                            className="h-full w-full"
                          />
                        </div>
                        <div>
                          <div className={`font-medium ${
                            currentSong?.id === song._id ? 'text-fuchsia-500' : 'text-white'
                          }`}>
                            {song.title}
                          </div>
                          <div className="text-sm text-slate-400 sm:hidden">
                            {song.artists?.join(', ')}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 hidden sm:table-cell text-slate-400 text-sm">
                      {song.artists?.join(', ')}
                    </td>
                    <td className="p-4 w-20 text-center">
                      <button
                        onClick={(e) => toggleFavorite(song._id, e)}
                        className="p-1 hover:scale-110 transition-transform"
                      >
                        <Heart className="w-5 h-5 text-fuchsia-500" fill="currentColor" />
                      </button>
                    </td>
                    <td className="p-4 text-right text-slate-400 text-sm w-20">
                      {mmss(song.durationInSeconds)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
}